import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
} from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { useManualEmblaDrag } from '../hooks/useManualEmblaDrag';
import { scrollBy, scrollProgress } from '../lib/embla';
import type { EmblaCarouselType } from '../vendor/embla-carousel';

export interface Carousel3DChange {
  selectedIndex: number;
  isMobile: boolean;
  carouselRadius: number;
}

interface Carousel3DProps<T> {
  items: T[];
  renderItem: (
    item: T,
    index: number,
    selectedIndex: number,
    ctx: { isMobile: boolean; carouselRadius: number; tiltEnabled: boolean }
  ) => React.ReactNode;
  onSelect?: (item: T) => void;
  onBack?: () => void;
  onChange?: (state: Carousel3DChange) => void;
  spacing?: number;
  parabola?: number;
  mobileSpacing?: number;
  mobileParabola?: number;
  forceMobileLayout?: boolean;
  backLabel?: string;
  hideBackButton?: boolean;
  tiltEnabled?: boolean;
  initialIndex?: number;
  onReInit?: (index: number) => void;
  onInit?: (api: EmblaCarouselType) => void;
}

export interface Carousel3DHandle {
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
  selectedIndex: number;
  onSettle: (cb: () => void) => () => void;
  isReady: boolean;
}

function Carousel3DInner<T>(
  {
    items,
    renderItem,
    onSelect,
    onBack,
    onChange,
    spacing,
    parabola,
    mobileSpacing,
    mobileParabola,
    forceMobileLayout,
    backLabel,
    hideBackButton,
    tiltEnabled = true,
    initialIndex,
    onReInit,
    onInit,
  }: Carousel3DProps<T>,
  ref: React.Ref<Carousel3DHandle>
) {
  const {
    emblaRef,
    emblaApi,
    selectedIndex,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleWheel,
    scrollPrev,
    scrollNext,
    isMobile,
    carouselRadius,
  } = useManualEmblaDrag({
    itemCount: items.length,
    spacing,
    parabola,
    mobileSpacing,
    mobileParabola,
    forceMobileLayout,
    initialIndex,
    onReInit,
    onInit,
  });

  useImperativeHandle(
    ref,
    () => ({
      scrollPrev,
      scrollNext,
      scrollTo: (index: number) => {
        if (!emblaApi) return;
        const progress = scrollProgress(emblaApi);
        const target = (index + 0.5) / items.length;
        let diff = target - progress;
        diff -= Math.round(diff);
        const engine = emblaApi.internalEngine();
        const distance = diff * engine.limit.length;
        scrollBy(emblaApi, distance);
      },
      selectedIndex,
      onSettle: (cb: () => void) => {
        console.log('Carousel3D: settle callback registered', { selectedIndex });
        const wrapped = () => {
          console.log('Carousel3D: settle callback executed', { selectedIndex });
          cb();
        };
        emblaApi?.on('settle', wrapped);
        return () => emblaApi?.off('settle', wrapped);
      },
      isReady: !!emblaApi,
    }),
    [scrollPrev, scrollNext, emblaApi, selectedIndex, items.length]
  );

  useEffect(() => {
    onChange?.({ selectedIndex, isMobile, carouselRadius });
  }, [selectedIndex, isMobile, carouselRadius, onChange]);

  const handleKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLDivElement>): void => {
      if (evt.key === 'ArrowLeft') {
        evt.preventDefault();
        scrollPrev();
      } else if (evt.key === 'ArrowRight') {
        evt.preventDefault();
        scrollNext();
      } else if ((evt.key === 'Enter' || evt.key === ' ') && onSelect) {
        evt.preventDefault();
        onSelect(items[selectedIndex]);
      } else if (evt.key === 'Escape' && onBack) {
        evt.preventDefault();
        onBack();
      }
    },
    [scrollPrev, scrollNext, onSelect, items, selectedIndex, onBack]
  );

  return (
    <div className="relative z-0">
      {onBack && !hideBackButton && (
        <button
          type="button"
          aria-label={backLabel ?? 'Back'}
          className={clsx(
            'absolute left-4 top-4 z-10 rounded-full border border-hairline bg-black/50 px-3 py-1 text-sm text-white backdrop-blur-md',
            'hover:bg-black/60 focus:outline-none focus:ring-2 focus:ring-accent'
          )}
          onClick={onBack}
        >
          {backLabel ?? 'Back'}
        </button>
      )}
      <motion.div
        className="embla z-10 outline-none"
        ref={emblaRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onDragStart={(e) => e.preventDefault()}
        onPointerDownCapture={handlePointerDown}
        onPointerMoveCapture={handlePointerMove}
        onPointerUpCapture={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onWheel={handleWheel}
        style={{ position: 'relative', perspective: isMobile ? 'none' : '1200px' }}
      >
        <div
          className="embla__container"
          style={{
            position: 'relative',
            transformStyle: isMobile ? 'flat' : 'preserve-3d',
            width: isMobile ? 'auto' : '100px',
            display: isMobile ? 'flex' : undefined,
          }}
        >
          {items.map((item, index) =>
            renderItem(item, index, selectedIndex, {
              isMobile,
              carouselRadius,
              tiltEnabled,
            })
          )}
        </div>
      </motion.div>
    </div>
  );
}

const Carousel3D = forwardRef(Carousel3DInner) as <T>(
  props: Carousel3DProps<T> & { ref?: React.Ref<Carousel3DHandle> }
) => React.ReactElement;

export default Carousel3D;

